import React, { useEffect, useState } from "react";
import { noOpNotifyContext } from "polygraphql";
import { useTranslation } from "utilities/i18n";
import { useSetDeviceSettingsWithXmlMutation } from "generated/graphql";
import { object, ObjectSchema } from "yup";
import { Form, Formik, InputProps, TextArea } from "collections/Form";
import { DISMISS, useNotification } from "collections/Notifications";
import { LoadingIndicator, PolyIcon, SubmitButton } from "elements";
import "./DeviceDetails.scss";
import { Field, FieldProps } from "formik";
import { xmlString } from "collections/Form";
import { Layout } from "views/Main/Layouts";
import {
  useDeviceDetailsReadOnly,
  useDeviceSettingsRouteMatch
} from "./DeviceDetailsRouter";
import { needs, useGuard } from "rbac";
import { Guard } from "TsRoleGuards";

interface GraphQLSettingError {
  name?: string;
  requestedValue?: string;
  result?: string;
}

const AdvancedSettings: React.FC<InputProps> = ({ name }) => {
  const { t } = useTranslation();
  const userCanEdit = useGuard(needs("update:device"));

  return (
    <>
      <label htmlFor="xml-editor" className="sr-only">
        {t("XML_EDITOR")}
      </label>
      <TextArea
        id="xml-editor"
        name={name}
        errorAbove={true}
        className="details"
        inputClassName="xml-editor"
        disabled={!userCanEdit}
      />
    </>
  );
};

const schema: ObjectSchema<any> = object().shape({
  deviceSettingsAsXML: xmlString().required()
});

const DeviceAdvancedSettings: React.FC<{
  data: string;
  onUpdate: () => void;
  onLoad: () => void;
  loading: boolean;
}> = ({ data, onUpdate, onLoad, loading }) => {
  const { t } = useTranslation();
  const {
    params: { deviceId: id }
  } = useDeviceSettingsRouteMatch();
  const device = useDeviceDetailsReadOnly(id);
  const [usefulError, setError] = useState(
    [] as Array<{
      fieldName: string;
      fieldValue: string;
      deviceResponse: string;
    }>
  );

  const [, submit] = useSetDeviceSettingsWithXmlMutation();
  const [addNotification] = useNotification();

  /**
   * Purely used to tell the parent that the route has been viewed
   * so it can derive whether an update to advanced settings query
   * will be necessary.
   */
  useEffect(() => {
    onLoad();
  }, [onLoad]);

  if (!data.length || loading) {
    return <LoadingIndicator />;
  }

  const validate = (values: { deviceSettingsAsXML: string }) => {
    if (!values.deviceSettingsAsXML || !values.deviceSettingsAsXML.length) {
      return {
        deviceSettingsAsXML: t("ERROR_MISSING_XML_CONTENT")
      };
    } else return {};
  };

  const { Content } = Layout;

  return (
    <Formik
      name="DeviceAdvanceSettings"
      customDimensions={{
        model: device?.hardwareModel,
        software_version: device?.softwareVersion
      }}
      initialValues={{ deviceSettingsAsXML: data }}
      validationSchema={schema}
      validate={validate}
      onSubmit={async (values: { deviceSettingsAsXML: string }) => {
        const { error } = await submit(
          {
            settingsXML: values.deviceSettingsAsXML as string,
            id
          },
          noOpNotifyContext
        );

        if (error) {
          let tempError: Array<{
            fieldName: string;
            fieldValue: string;
            deviceResponse: string;
          }> = error.graphQLErrors
            .filter(gqe => gqe?.extensions?.errors?.length > 0)
            .flatMap(gqe => gqe?.extensions?.errors)
            .map((err: GraphQLSettingError) => ({
              fieldName: `${err.name}`,
              fieldValue: `${err.requestedValue}`,
              deviceResponse: `${err.result}`
            }));

          setError(tempError);

          // Only show the saving device error IF there's no banner-style errors to display
          if (!tempError.length) {
            addNotification({
              type: "error",
              id: "deviceSettingsError",
              message: t("ERROR_SAVING_DEVICE_SETTINGS_MESSAGE", {
                message: error.message || ""
              }),
              dismiss: "USER_ONLY"
            });
          }
          // According to Team MO, the absence of an error MEANS success....
          // } else if (data?.setDeviceSettingsWithXML.fetchedAt) {
        } else {
          // reset error state on success
          setError([]);

          addNotification({
            type: "success",
            message: t("DEVICE_SETTINGS_UPDATED"),
            dismiss: DISMISS.DEFAULT
          });

          onUpdate();
        }
      }}
    >
      <Form className="form">
        <div className="header-information grid pb-1">
          <div className="col-6 col-xl-8 v-bottom">
            {t("ADVANCED_USERS_ONLY")}
          </div>
          <Guard requirement="update:device">
            <div
              className="col-6 col-xl-4 text-right"
              data-testid="save-settings-buttons"
            >
              <Field name="reset">
                {({ form: { dirty, handleReset } }: FieldProps) => {
                  return (
                    <button
                      type="button"
                      className="btn"
                      disabled={!dirty}
                      onClick={handleReset}
                    >
                      {t("DISCARD")}
                    </button>
                  );
                }}
              </Field>
              <Field name="submit">
                {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
                  <SubmitButton
                    isSubmitting={isSubmitting}
                    disabled={!dirty || !isValid}
                  />
                )}
              </Field>
            </div>
          </Guard>
        </div>
        <Content.ScrollingList>
          {!!usefulError.length && (
            <div className="banner banner-error d-flex block mb-1 pv-1 pr-2">
              <div>
                <PolyIcon icon="warning_triangle" className="mr-1" />
              </div>
              <div>
                <p>{t("ADVANCED_ERROR_INTRO")}</p>
                {usefulError.map((e, i) => (
                  <div className="d-flex mt-2" key={`error-${i}`}>
                    <div className="error pr-1">Error {i + 1}</div>
                    <div className="">
                      <p className="error-field-name mb-5">
                        <span className="error-label">Field Name:</span>
                        <span className="error-value ml-5">{e.fieldName}</span>
                      </p>
                      <p className="error-field-value mb-5">
                        <span className="error-label">Field Value:</span>
                        <span className="error-value ml-5">{e.fieldValue}</span>
                      </p>
                      <p className="error-device-response mb-5">
                        <span className="error-label">Device Response:</span>
                        <span className="error-value ml-5">
                          {e.deviceResponse}
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <AdvancedSettings name="deviceSettingsAsXML" />
        </Content.ScrollingList>
      </Form>
    </Formik>
  );
};

export default DeviceAdvancedSettings;
